import * as React from 'react';
import { IAthlete, IGroup, ITeam } from '../../../../data/types';
import { useListSearch } from '../../../../hooks/useListSearch';
import { FullHeightColumn } from '../../../layout/Column';
import { Row } from '../../../layout/Row';
import { ScrollableArea } from '../../../layout/ScrollableArea';
import { ClearAllButton, SelectAllButton } from '../../../shared/Button';
import { Count } from '../../../shared/Count';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { ListControlLabel } from '../../../shared/form/ListControlLabel';
import { SelectBox } from '../../../shared/form/SelectBox';
import { TextField } from '../../../shared/form/TextField';
import { InlineUploader } from '../../../shared/InlineUploader';
import { ListControlContainer } from '../../../shared/List/ListControlContainer';
import { MultiAthleteSelectList } from '../athletes/MultiAthleteSelectList';
import { MultiGroupSelectList } from '../groups/MultiGroupSelectList';

interface IGroupManagementProps {
  groups: IGroup[];
  selectedGroups: IGroup[];
  groupSelectionHandler: (group: IGroup, checked: boolean) => void;
  clearAllGroups: () => void;
  selectAllGroups: () => void;
}
interface IProps {
  team: Partial<ITeam>;
  teams: ITeam[];
  onChange: (name: string, value: string | null) => void;
  error?: string;
  isEdit: boolean;
}

interface IAthleteManagementProps {
  athletes: IAthlete[];
  selectedAthletes: IAthlete[];
  athleteSelectionHandler: (athlete: IAthlete, checked: boolean) => void;
  clearAllAthletes: () => void;
  selectAllAthletes: () => void;
  aggregateOnCreate: boolean;
  setAggregateOnCreate: (value: boolean) => void;
}

export const TeamForm = (
  props: IProps & IGroupManagementProps & IAthleteManagementProps
): React.ReactElement => {
  const {
    list: athletes,
    search: searchAthletes,
    clear: clearAthleteSearch,
    searchTerm: athleteSearchTerm,
  } = useListSearch(props.athletes);

  const {
    list: groups,
    search: searchGroups,
    clear: clearGroupSearch,
    searchTerm: groupSearchTerm,
  } = useListSearch(props.groups);
  /**
   * @description handle name change
   * @param value
   */
  const handleNameChange = (value: string): void =>
    props.onChange('name', value);

  /**
   * @description handle image change
   * @param value
   */
  const handleImageChange = (f: { url: string }): void =>
    props.onChange('image', f.url);

  const handleImageDelete = (): void => props.onChange('image', null);

  return (
    <Row shouldCollapse>
      <FullHeightColumn style={{ display: 'flex-start', padding: '10px' }}>
        <Row style={{ justifyContent: 'center', flex: 0 }}>
          <InlineUploader
            onChange={handleImageChange}
            onDelete={handleImageDelete}
            path={props.team?.image ?? ''}
          />
        </Row>
        <ControlSpacer>
          <TextField
            name='name'
            type='text'
            label='Team Name'
            onChange={handleNameChange}
            placeholder='Enter the team name...'
            value={props.team?.name ?? ''}
            error={props.error}
          />
        </ControlSpacer>
        {!props.isEdit && (
          <div
            style={{
              paddingTop: '10px',
            }}
          >
            <SelectBox
              style={{ justifyContent: 'flex-start' }}
              label='Include Historical Athlete Data in Team'
              selected={props.aggregateOnCreate}
              onSelect={(e: React.ChangeEvent<HTMLInputElement>): void =>
                props.setAggregateOnCreate(e.target.checked)
              }
            />
          </div>
        )}
      </FullHeightColumn>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Group(s) (optional)'
          onSearchClearHandler={clearGroupSearch}
          searchValue={groupSearchTerm}
          onSearchHandler={searchGroups}
        >
          <ClearAllButton
            onClick={props.clearAllGroups}
            disabled={props.selectedGroups.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllGroups}
            disabled={props.selectedGroups.length === props.groups.length}
          />
          <Count value={props.selectedGroups.length} />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiGroupSelectList
              teams={props.teams}
              selectedTeams={props.teams}
              disabled={props.teams.length < 1}
              groups={groups}
              selectedGroups={props.selectedGroups}
              onSelect={props.groupSelectionHandler}
              flat
              emptyMessage={
                groupSearchTerm.length > 0
                  ? `No groups matching '${groupSearchTerm}'`
                  : null
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Athletes (optional)'
          onSearchClearHandler={clearAthleteSearch}
          searchValue={athleteSearchTerm}
          onSearchHandler={searchAthletes}
        >
          <ClearAllButton
            onClick={props.clearAllAthletes}
            disabled={props.selectedAthletes.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllAthletes}
            disabled={props.selectedAthletes.length === props.athletes.length}
          />
          <Count
            value={props.selectedAthletes.length}
            style={{
              alignSelf: 'flex-end',
            }}
          />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiAthleteSelectList
              athletes={athletes}
              teams={props.teams}
              selectedTeams={props.teams}
              selectedAthletes={props.selectedAthletes}
              onSelect={props.athleteSelectionHandler}
              clearSelected={props.clearAllAthletes}
              disabled={!Boolean(props.teams.length)}
              flat
              emptyMessage={
                athleteSearchTerm.length > 0
                  ? `No athletes matching '${athleteSearchTerm}'`
                  : null
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
    </Row>
  );
};
